import React from 'react';
import {
  Phone, PhoneOff, Mic, MicOff, Volume2, VolumeX, Pause, Play,
  PhoneForwarded, Hash, User, MessageSquare, Sparkles,
} from 'lucide-react';
import { DIALPAD_BUTTONS } from './DialPad';

const CALL_STATES = {
  IDLE: 'idle',
  CONNECTING: 'connecting',
  RINGING: 'ringing',
  CONNECTED: 'connected',
  ON_HOLD: 'on_hold',
  ENDED: 'ended',
};

const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const STATUS_BG = {
  [CALL_STATES.CONNECTING]: 'bg-teal-400/20',
  [CALL_STATES.RINGING]: 'bg-yellow-400/20 animate-pulse',
  [CALL_STATES.CONNECTED]: 'bg-green-400/20',
  [CALL_STATES.ON_HOLD]: 'bg-yellow-400/20',
  [CALL_STATES.ENDED]: 'bg-red-400/20',
};

const STATUS_FG = {
  [CALL_STATES.CONNECTING]: 'text-teal-400',
  [CALL_STATES.RINGING]: 'text-yellow-400',
  [CALL_STATES.CONNECTED]: 'text-green-400',
  [CALL_STATES.ON_HOLD]: 'text-yellow-400',
  [CALL_STATES.ENDED]: 'text-red-400',
};

const STATUS_TEXT = {
  [CALL_STATES.CONNECTING]: 'Connecting...',
  [CALL_STATES.RINGING]: 'Ringing...',
  [CALL_STATES.ON_HOLD]: 'On Hold',
  [CALL_STATES.ENDED]: 'Call Ended',
};

/**
 * In-call controls (mute / speaker / hold / keypad), in-call DTMF dialpad,
 * transfer UX, AI Assist preview and the End Call button.
 */
export const CallControls = ({
  callState,
  callDuration,
  callerNumber,
  isMuted,
  isSpeakerOn,
  showDialpad,
  showTransfer,
  transferNumber,
  aiSuggestions,
  sentiment,
  showAIAssist,
  onToggleMute,
  onToggleSpeaker,
  onToggleHold,
  onToggleDialpad,
  onSendDTMF,
  onToggleTransfer,
  onTransferNumberChange,
  onTransfer,
  onEndCall,
}) => (
  <>
    <div className="text-center mb-6">
      <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 ${STATUS_BG[callState] || 'bg-gray-500/20'}`}>
        <User className={`w-10 h-10 ${STATUS_FG[callState] || 'text-gray-400'}`} />
      </div>
      <p className="text-white text-lg font-medium" data-testid="dialer-caller-number">{callerNumber || 'Unknown'}</p>
      <p className="text-gray-400 text-sm capitalize" data-testid="dialer-call-status">
        {callState === CALL_STATES.CONNECTED ? formatDuration(callDuration) : STATUS_TEXT[callState]}
      </p>
    </div>

    {(callState === CALL_STATES.CONNECTED || callState === CALL_STATES.ON_HOLD) && (
      <div className="grid grid-cols-4 gap-3 mb-4">
        <button
          onClick={onToggleMute}
          className={`p-3 rounded-xl flex flex-col items-center gap-1 transition-colors ${
            isMuted ? 'bg-red-500/20 text-red-400' : 'bg-[#1a1a1f] text-gray-400 hover:bg-[#252530]'
          }`}
          data-testid="mute-button"
        >
          {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
          <span className="text-xs">Mute</span>
        </button>

        <button
          onClick={onToggleSpeaker}
          className={`p-3 rounded-xl flex flex-col items-center gap-1 transition-colors ${
            !isSpeakerOn ? 'bg-red-500/20 text-red-400' : 'bg-[#1a1a1f] text-gray-400 hover:bg-[#252530]'
          }`}
          data-testid="speaker-button"
        >
          {isSpeakerOn ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          <span className="text-xs">Speaker</span>
        </button>

        <button
          onClick={onToggleHold}
          className={`p-3 rounded-xl flex flex-col items-center gap-1 transition-colors ${
            callState === CALL_STATES.ON_HOLD ? 'bg-yellow-500/20 text-yellow-400' : 'bg-[#1a1a1f] text-gray-400 hover:bg-[#252530]'
          }`}
          data-testid="hold-button"
        >
          {callState === CALL_STATES.ON_HOLD ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
          <span className="text-xs">Hold</span>
        </button>

        <button
          onClick={onToggleDialpad}
          className={`p-3 rounded-xl flex flex-col items-center gap-1 transition-colors ${
            showDialpad ? 'bg-teal-400/20 text-teal-400' : 'bg-[#1a1a1f] text-gray-400 hover:bg-[#252530]'
          }`}
          data-testid="keypad-button"
        >
          <Hash className="w-5 h-5" />
          <span className="text-xs">Keypad</span>
        </button>
      </div>
    )}

    {showDialpad && callState === CALL_STATES.CONNECTED && (
      <div className="grid grid-cols-3 gap-2 mb-4">
        {DIALPAD_BUTTONS.flat().map((digit) => (
          <button
            key={digit}
            onClick={() => onSendDTMF(digit)}
            className="py-3 bg-[#1a1a1f] hover:bg-[#252530] border border-gray-700 rounded-lg text-white text-lg font-medium transition-colors"
            data-testid={`dialer-dtmf-${digit}`}
          >
            {digit}
          </button>
        ))}
      </div>
    )}

    {callState === CALL_STATES.CONNECTED && (
      <button
        onClick={onToggleTransfer}
        className="w-full mb-4 p-3 bg-[#1a1a1f] hover:bg-[#252530] border border-gray-700 rounded-xl flex items-center justify-center gap-2 text-gray-300 transition-colors"
        data-testid="transfer-toggle-btn"
      >
        <PhoneForwarded className="w-5 h-5" />
        Transfer Call
      </button>
    )}

    {showTransfer && (
      <div className="mb-4 flex gap-2">
        <input
          type="tel"
          value={transferNumber}
          onChange={(e) => onTransferNumberChange(e.target.value)}
          placeholder="Transfer to..."
          className="flex-1 bg-[#1a1a1f] border border-gray-700 rounded-lg px-3 py-2 text-white outline-none focus:border-teal-400"
          data-testid="transfer-number-input"
        />
        <button
          onClick={onTransfer}
          className="px-4 bg-teal-400 hover:bg-teal-500 rounded-lg text-black font-medium"
          data-testid="transfer-confirm-btn"
        >
          Transfer
        </button>
      </div>
    )}

    {showAIAssist && callState === CALL_STATES.CONNECTED && (
      <div className="mb-4 p-3 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-purple-400" />
          <span className="text-purple-400 text-sm font-medium">AI Assist</span>
          <span className={`ml-auto px-2 py-0.5 rounded text-xs ${
            sentiment === 'positive' ? 'bg-green-500/20 text-green-400' :
            sentiment === 'negative' ? 'bg-red-500/20 text-red-400' :
            'bg-gray-500/20 text-gray-400'
          }`}>
            {sentiment}
          </span>
        </div>
        <div className="text-gray-300 text-sm mb-2">
          <MessageSquare className="w-3 h-3 inline mr-1" />
          Listening...
        </div>
        {aiSuggestions.length > 0 && (
          <div className="space-y-1">
            {aiSuggestions.map((suggestion) => (
              <div key={suggestion} className="text-xs text-gray-400 flex items-start gap-1">
                <span className="text-purple-400">→</span>
                {suggestion}
              </div>
            ))}
          </div>
        )}
      </div>
    )}

    <button
      onClick={onEndCall}
      className="w-full py-4 bg-red-500 hover:bg-red-600 rounded-xl flex items-center justify-center gap-2 text-white font-semibold transition-colors"
      data-testid="end-call-button"
    >
      <PhoneOff className="w-6 h-6" />
      End Call
    </button>
  </>
);

export { CALL_STATES };
